/** Deep-link query params preserved from Properties / Opportunities / Reports shortcuts. */
export type OrderCreatePrefill = {
  buildingId?: number;
  buildingName?: string;
  tenantName?: string;
  /** Maps to form `suiteNumber` on submit. */
  suite?: string;
  /** Provenance tag persisted on `request.source` (#58219 / #58227). */
  source?: string;
  /** Cross-module service type name (Report Modifications, etc.). */
  serviceType?: string;
  serviceTypeId?: number;
  /**
   * Property address fields. Not parsed from URL params — supplied by in-app
   * navigation state (e.g. the Drawing Vault "Create order from annotation"
   * flow, which knows the building's address). Prefilling these keeps the SSI
   * form's building-locked address valid and populates the non-SSI form.
   */
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  /** Building code when supplied by navigation state; omitted when only buildingId is known. */
  buildingCode?: string;
  /** Default notes/comments text (e.g. a reference back to the source annotation). */
  notes?: string;
  /**
   * Client (requester) identity. Not parsed from URL params — supplied by in-app
   * navigation state (the Drawing Vault "Create order from annotation" flow seeds
   * the logged-in user so the SSI intake form isn't empty). The SSI form's email
   * combobox and company auto-fill still let the operator swap to a different
   * client. The non-SSI form ignores these (it always uses the caller's identity).
   */
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  companyId?: number;
};

/** Service types auto-set from cross-module entry points (#58227). */
export const PREFILL_AUTO_SERVICE_TYPES = [
  'Report Modifications',
  'Report Finalization',
  'SAB Modifications',
] as const;

export type PrefillAutoServiceType = (typeof PREFILL_AUTO_SERVICE_TYPES)[number];

const PREFILL_PARAM_KEYS = [
  'buildingId',
  'buildingName',
  'tenantName',
  'suite',
  'source',
  'serviceType',
  'changesRequested',
  'serviceTypeId',
] as const;

const AUTO_SERVICE_TYPE_SET = new Set<string>(PREFILL_AUTO_SERVICE_TYPES);

type ServiceTypeCatalogItem = {
  serviceTypeId: number;
  serviceName: string | null;
};

function parsePositiveInt(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return undefined;
  return value;
}

function parseTrimmedString(raw: string | null, maxLength: number): string | undefined {
  const trimmed = raw?.trim();
  if (!trimmed || trimmed.length > maxLength) return undefined;
  return trimmed;
}

/**
 * Resolves a cross-module service type name from URL params.
 * Accepts `serviceType` or legacy `changesRequested` (Report Updates enum).
 */
function parsePrefillServiceType(params: URLSearchParams): string | undefined {
  const direct = parseTrimmedString(params.get('serviceType'), 255);
  if (direct && AUTO_SERVICE_TYPE_SET.has(direct)) return direct;

  const fromChanges = parseTrimmedString(params.get('changesRequested'), 255);
  if (fromChanges && AUTO_SERVICE_TYPE_SET.has(fromChanges)) return fromChanges;

  return undefined;
}

/**
 * Parses `/orders/new?…` search params into form default values.
 * Invalid or empty values are omitted so the form starts clean.
 */
export function parseOrderCreatePrefill(searchParams: URLSearchParams): OrderCreatePrefill {
  const prefill: OrderCreatePrefill = {};

  const buildingId = parsePositiveInt(searchParams.get('buildingId'));
  if (buildingId) prefill.buildingId = buildingId;

  const buildingName = parseTrimmedString(searchParams.get('buildingName'), 500);
  if (buildingName) prefill.buildingName = buildingName;

  const tenantName = parseTrimmedString(searchParams.get('tenantName'), 500);
  if (tenantName) prefill.tenantName = tenantName;

  const suite = parseTrimmedString(searchParams.get('suite'), 50);
  if (suite) prefill.suite = suite;

  const source = parseTrimmedString(searchParams.get('source'), 100);
  if (source) prefill.source = source;

  const serviceTypeId = parsePositiveInt(searchParams.get('serviceTypeId'));
  if (serviceTypeId) prefill.serviceTypeId = serviceTypeId;

  const serviceType = parsePrefillServiceType(searchParams);
  if (serviceType) prefill.serviceType = serviceType;

  return prefill;
}

/**
 * Maps a prefill service type name to a catalog id once service types have loaded.
 */
export function resolvePrefillServiceTypeIds(
  prefill: OrderCreatePrefill,
  serviceTypes: ServiceTypeCatalogItem[],
): OrderCreatePrefill {
  if (prefill.serviceTypeId != null || !prefill.serviceType) return prefill;
  const match = serviceTypes.find((item) => item.serviceName?.trim() === prefill.serviceType);
  if (!match) return prefill;
  return { ...prefill, serviceTypeId: match.serviceTypeId };
}

/** Param keys echoed by legacy create shortcuts — used for tests and docs. */
export const ORDER_CREATE_PREFILL_PARAM_KEYS = PREFILL_PARAM_KEYS;
